// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { onAuthStateChanged, signOut, updateProfile, getRedirectResult } from 'firebase/auth'
import { doc, onSnapshot, updateDoc, setDoc, collection, query, where } from 'firebase/firestore'
import {
  auth, db, loginWithEmail, signupWithEmail,
  loginWithGoogle as _loginWithGoogle,
  resetPassword as _resetPassword,
  ensureUser, updateStreakOnLogin, runBadgeAudit,
} from '../utils/firestore'
import { LEVELS, levelFromXP } from '../data/subjects'
import LoadingScreen from '../components/LoadingScreen'
import { isFreshReferral } from '../utils/referralReward'

const AuthContext = createContext(null)

// awardXP now writes a `level` field alongside xp (see firestore.js) so most reads get a correct
// value straight from Firestore — but that's a best-effort convenience field, not something to
// actually depend on: any write path that changes xp without also going through awardXP (there's
// one server-side, in netlify/functions/friends.js, deliberately not duplicating the level math
// for a single non-critical field) would leave it stale, which is exactly the class of bug that
// caused profile.level to be permanently wrong in the first place. Recomputing it fresh here,
// every time profile data enters the app, means the level-up detection below (and everything
// else that reads profile.level) is correct regardless of whether the persisted value happens to
// be current.
function withLevel(data) {
  return data ? { ...data, level: levelFromXP(data.xp || 0) } : data
}

export function AuthProvider({ children }) {
  const [user,               setUser]               = useState(null)
  const [profile,            setProfile]            = useState(null)
  const [loading,            setLoading]            = useState(true)
  const [streakCelebration,  setStreakCelebration]  = useState(null) // { streak: N } when streak just went up
  const prevStreakRef = useRef(null)
  // Referrer-side referral reward. Mirrors the streak pattern: the 'referral' badge (see
  // src/data/badges.js) is awarded exactly once, server-side, the moment someone signs up with
  // this user's code (netlify/functions/referral.js) — so "badges array just gained 'referral'
  // since the last snapshot" is a reliable one-shot trigger, the same way "streak just went up" is.
  // The REFERRED side doesn't need this — they get an immediate response from the apply call and
  // are shown their popup locally, right where that happens (see Dashboard.jsx).
  const [referralReward,     setReferralReward]     = useState(null) // { variant: 'referrer' } when just earned
  const prevBadgesRef = useRef(null)
  // New incoming friend request. Friends.jsx only ever showed a passive badge count on its
  // Requests tab — nothing fired when a request actually arrived, and only while already on that
  // page. This needs its own listener (friendRequests is a top-level collection, not a field on
  // the user doc, so it can't piggyback on the profile snapshot above).
  const [newFriendRequest,   setNewFriendRequest]   = useState(null) // { id, from, fromName } when one just arrives
  const seenRequestIdsRef = useRef(null) // null until first snapshot resolves, then a Set
  // Level-up popup. Same shape of detection as streak — profile.level now actually gets written
  // by awardXP (see firestore.js), so this can fire the same way "streak just went up" does.
  const [levelUp,            setLevelUp]            = useState(null) // { level, title } when level just went up
  const prevLevelRef = useRef(null)
  // Post-onboarding XP recap. Deliberately NOT shown the instant onboarding's save completes —
  // Onboarding.jsx stashes the breakdown on the profile and navigates straight to the dashboard,
  // where the tour then runs. This fires once the tour's own onComplete has actually flipped
  // profile.tourComplete to true, exactly mirroring how the badge/streak checks below detect a
  // one-shot transition rather than a static value.
  const [onboardingRecap,    setOnboardingRecap]    = useState(null)
  // Why a Google redirect sign-in failed (a Firebase error code), so the sign-in pages can tell the person instead of the
  // failure only reaching the console. Cleared by clearRedirectError.
  const [redirectError,      setRedirectError]      = useState(null)
  const prevTourCompleteRef = useRef(null)
  // Referred-side referral popup: set once per sign-in so a later snapshot cannot show it twice (see the profile listener).
  const referredShownRef = useRef(false)

  useEffect(() => {
    let profileUnsub = () => {}
    let requestsUnsub = () => {}

    // Standalone/installed PWAs use signInWithRedirect for Google sign-in (see loginWithGoogle
    // in firestore.js) because signInWithPopup's window.opener handoff doesn't reliably work once
    // the app is running in its own standalone window rather than a normal browser tab — the
    // popup either fails to open or never reports its result back. The redirect flow instead
    // navigates the whole app to Google and back; onAuthStateChanged below picks up the resulting
    // session automatically once the app reloads, this just surfaces an error if the redirect
    // itself failed (e.g. an existing account with a different sign-in method). The code is exposed as redirectError.
    getRedirectResult(auth).catch(e => {
      console.error('[AuthContext] Google redirect sign-in failed:', e.code)
      setRedirectError(e.code || 'unknown')
    })

    const authUnsub = onAuthStateChanged(auth, async u => {
      setUser(u)
      profileUnsub()
      requestsUnsub()

      if (u) {
        try {
          await ensureUser(u.uid, {
            displayName: u.displayName || '',
            email:       u.email       || '',
            avatarUrl:   u.photoURL    || '',
          })
          await updateStreakOnLogin(u.uid)
          // Run badge audit if it hasn't run in the last 7 days
          try {
            const userSnap = await import('firebase/firestore').then(m =>
              m.getDoc(m.doc(db, 'users', u.uid))
            )
            const lastAudit = userSnap.data()?.lastBadgeAudit?.toDate?.()
            const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
            if (!lastAudit || lastAudit < sevenDaysAgo) {
              runBadgeAudit(u.uid).catch(e => console.warn('[badge audit]', e))
            }
          } catch (e) { /* non-fatal */ }
        } catch (e) {
          console.error('[AuthContext] ensureUser/streak error:', e)
        }

        profileUnsub = onSnapshot(doc(db, 'users', u.uid), snap => {
          const data = snap.exists() ? withLevel({ uid: u.uid, ...snap.data() }) : null
          const readyForCelebrations = !!(data && data.onboardingComplete && data.tourComplete)
          // Detect streak increase — fire celebration if streak went up. Gated behind onboarding
          // AND tour being complete: updateStreakOnLogin runs on every login including the very
          // first one before onboarding even starts, and streak going 0→1 there was popping the
          // celebration mid-onboarding-wizard. Gating at render time (rather than skipping
          // detection here) means if it fires while gated, the state just waits — once the gate
          // opens the already-set value shows, nothing is lost or needs re-triggering.
          if (data && prevStreakRef.current !== null) {
            const prev = prevStreakRef.current
            const curr = data.streak || 0
            // A legitimate streak increase is always exactly +1 (one more day of activity since
            // the last check). A jump bigger than that only happens when Firestore's local cache
            // returns an old value first (e.g. a brand-new device with no cache yet, or a slow
            // network) and the real value arrives a moment later — celebrating that as "you just
            // extended your streak" is what was re-showing this popup on new devices for a streak
            // the account already had.
            if (curr === prev + 1 && curr > 0 && readyForCelebrations) {
              setStreakCelebration({ streak: curr })
            }
          }
          if (data) prevStreakRef.current = data.streak || 0
          // Detect the 'referral' badge newly appearing — this user just successfully referred
          // someone (see netlify/functions/referral.js: awardReferralBadge runs on the referrer's
          // doc the moment a new signup applies their code). Skipped on the very first snapshot
          // for the same reason the streak check is — otherwise every existing Recruiter would get
          // the popup again on their next login.
          if (data && prevBadgesRef.current !== null) {
            const prevBadges = prevBadgesRef.current
            const currBadges = data.badges || []
            // referralRewardShown is the actual guard against repeats — the badges-array
            // transition is just what decides WHEN to check it, so this still only evaluates once
            // right after the badge appears rather than on every snapshot. Without the persisted
            // flag, the same cache-race as streak/level could replay this on a new device: a
            // stale first snapshot without 'referral' yet, followed by the true one that has it,
            // looks identical to "just earned it" even when it was earned days ago on another device.
            if (currBadges.includes('referral') && !prevBadges.includes('referral') && !data.referralRewardShown) {
              setReferralReward({ variant: 'referrer' })
              updateDoc(doc(db, 'users', u.uid), { referralRewardShown: true }).catch(() => {})
            }
          }
          if (data) prevBadgesRef.current = data.badges || []
          // The REFERRED side. netlify/functions/referral.js stamps referredBy and referredAt on the new account the moment a
          // code is applied, usually while the Signup page is still on screen. Signup unmounts as soon as the profile exists,
          // so it cannot show the popup itself; showing it from here puts it in front of the new user wherever they are
          // (onboarding). Only a referral stamped in the last few minutes counts (utils/referralReward.js), so accounts
          // referred long ago never see it, and referredRewardShown stops another device repeating it.
          if (data && !referredShownRef.current && isFreshReferral(data)) {
            referredShownRef.current = true
            setReferralReward({ variant: 'referred' })
            updateDoc(doc(db, 'users', u.uid), { referredRewardShown: true }).catch(() => {})
          }
          // Detect level increase — same shape as streak, same gating rationale (onboarding alone
          // can be enough XP to cross a level threshold; that shouldn't interrupt the wizard either).
          if (data && prevLevelRef.current !== null) {
            const prev = prevLevelRef.current
            const curr = data.level || 1
            if (curr > prev && curr > (data.highestCelebratedLevel || 0) && readyForCelebrations) {
              setLevelUp({ level: curr, title: LEVELS[curr - 1]?.title || 'Newcomer' })
              updateDoc(doc(db, 'users', u.uid), { highestCelebratedLevel: curr }).catch(() => {})
            }
          }
          if (data) prevLevelRef.current = data.level || 1
          // Detect tourComplete flipping to true — the actual trigger for the onboarding XP
          // recap. onboardingXpBreakdown is written once, during Onboarding.jsx's finish(); once
          // shown, onboardingRecapShown is set so this can never fire a second time for this user.
          if (data && prevTourCompleteRef.current === false && data.tourComplete === true) {
            if (data.onboardingXpBreakdown && !data.onboardingRecapShown) {
              setOnboardingRecap(data.onboardingXpBreakdown)
            }
          }
          if (data) prevTourCompleteRef.current = !!data.tourComplete
          setProfile(data)
          setLoading(false)
        }, e => {
          console.error('[AuthContext] profile listener error:', e)
          setLoading(false)
        })

        // Separate listener: friendRequests is a top-level collection (see firestore.js
        // sendFriendRequest), not a field on the user doc, so it needs its own subscription.
        requestsUnsub = onSnapshot(
          query(collection(db, 'friendRequests'), where('to', '==', u.uid)),
          snap => {
            const currIds = new Set(snap.docs.map(d => d.id))
            if (seenRequestIdsRef.current !== null) {
              const newDoc = snap.docs.find(d => !seenRequestIdsRef.current.has(d.id))
              if (newDoc) {
                setNewFriendRequest({ id: newDoc.id, ...newDoc.data() })
              }
            }
            seenRequestIdsRef.current = currIds
          },
          e => console.error('[AuthContext] friend requests listener error:', e)
        )
      } else {
        setProfile(null)
        setLoading(false)
        prevBadgesRef.current = null
        seenRequestIdsRef.current = null
        referredShownRef.current = false
      }
    })

    return () => { authUnsub(); profileUnsub(); requestsUnsub() }
  }, [])

  const refreshProfile = useCallback(async () => {
    if (!user) return
    const { getDoc, doc: fsDoc } = await import('firebase/firestore')
    const snap = await getDoc(fsDoc(db, 'users', user.uid))
    if (snap.exists()) setProfile(withLevel({ uid: user.uid, ...snap.data() }))
  }, [user])

  const login = (email, pw) => loginWithEmail(email, pw)

  // signup: creates the Firebase Auth user AND records the display name. The account exists once signupWithEmail has
  // resolved, so neither name write below may turn a created account into a failed sign-up (a retry would only say the
  // email is already in use). The nested profile map is written as a map: with setDoc a dotted key is a literal field name.
  const signup = async (email, pw, displayName) => {
    const cred = await signupWithEmail(email, pw)
    if (displayName && cred?.user) {
      // Set displayName on the Firebase Auth user object
      try { await updateProfile(cred.user, { displayName }) } catch (e) { /* ensureUser patches the name later */ }
      // Also write it to Firestore immediately so leaderboard/referral queries work
      try {
        await setDoc(doc(db, 'users', cred.user.uid), {
          displayName,
          profile: { displayName },
        }, { merge: true })
      } catch (e) { /* non-fatal: ensureUser will handle this */ }
    }
    return cred
  }

  const loginWithGoogle = () => _loginWithGoogle()
  const resetPassword   = email => _resetPassword(email)
  const logout          = () => signOut(auth)

  return (
    <AuthContext.Provider value={{
      user, profile, loading, login, signup, loginWithGoogle, resetPassword, logout, refreshProfile,
      streakCelebration, clearStreakCelebration: () => setStreakCelebration(null),
      referralReward, clearReferralReward: () => setReferralReward(null),
      newFriendRequest, clearNewFriendRequest: () => setNewFriendRequest(null),
      levelUp, clearLevelUp: () => setLevelUp(null),
      onboardingRecap,
      redirectError, clearRedirectError: () => setRedirectError(null),
      clearOnboardingRecap: async () => {
        setOnboardingRecap(null)
        if (user) {
          try { await updateDoc(doc(db, 'users', user.uid), { onboardingRecapShown: true }) } catch (e) {}
        }
      },
    }}>
      {loading ? <LoadingScreen /> : children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
